// src/components/layout/Sidebar.js
import React, { useState, useEffect, useRef } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { usePartner } from '../../lib/usePartner'
import { theme } from '../../lib/theme'

export default function Sidebar() {
  const navigate = useNavigate()
  const { partnerProfile } = usePartner()
  const [profile, setProfile] = useState(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user || cancelled) return
      const { data } = await supabase
        .from('profiles')
        .select('id, full_name, email, subscription_status')
        .eq('id', user.id)
        .single()
      if (!cancelled) setProfile(data)
    })()
    return () => { cancelled = true }
  }, [])

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [menuOpen])

  const handleLogout = async () => {
    setMenuOpen(false)
    await supabase.auth.signOut()
    navigate('/login')
  }

  const goTo = (path) => {
    setMenuOpen(false)
    navigate(path)
  }

  const navItems = [
    { path: '/',         label: 'This Month', end: true },
    { path: '/plan',     label: 'Plan' },
    { path: '/log',      label: 'Log' },
    { path: '/networth', label: 'Net Worth' },
    { path: '/money-movement', label: 'Money Movement' },
    { path: '/debts',    label: 'Debts' },
    { path: '/checkins', label: 'Check-ins' },
    { path: '/report',   label: 'Reports' },
  ]

  const initial = (profile?.full_name || profile?.email || '?').trim().charAt(0).toUpperCase()
  const displayName = (profile?.full_name || '').trim().split(/\s+/)[0] || (profile?.email || '').split('@')[0] || 'You'
  const partnerFirst = partnerProfile?.full_name?.trim().split(/\s+/)[0]
  const subStatus = profile?.subscription_status
  const planLabel = subStatus === 'active' || subStatus === 'partner' || subStatus === 'pro'
    ? 'Pro'
    : subStatus === 'trial'
      ? 'Trial'
      : 'Free'
  const profileSub = partnerFirst ? `${planLabel} · with ${partnerFirst}` : planLabel

  return (
    <aside style={styles.sidebar}>
      {/* Brand */}
      <div style={styles.brand}>
        <span style={styles.brandMerq}>Merq</span>
        <span style={styles.brandVault}>Vault</span>
      </div>

      {/* Primary nav */}
      <nav style={styles.nav}>
        {navItems.map(item => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.end}
            className="btn-compact"
            style={({ isActive }) => ({
              ...styles.navItem,
              ...(isActive ? styles.navItemActive : {}),
            })}
          >
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Foot: profile chip with dropdown */}
      <div style={styles.foot} ref={menuRef}>
        {menuOpen && (
          <div style={styles.menu} role="menu">
            <button
              type="button"
              role="menuitem"
              className="btn-compact"
              style={styles.menuItem}
              onClick={() => goTo('/settings')}
            >
              <span>Settings</span>
            </button>
            <button
              type="button"
              role="menuitem"
              className="btn-compact"
              style={styles.menuItem}
              onClick={() => goTo('/help')}
            >
              <span>Help</span>
            </button>
            <div style={styles.menuDivider} />
            <button
              type="button"
              role="menuitem"
              className="btn-compact"
              style={styles.menuItem}
              onClick={handleLogout}
            >
              <span>Sign out</span>
            </button>
          </div>
        )}

        <button
          type="button"
          className="btn-compact"
          style={styles.profileCard}
          onClick={() => setMenuOpen(o => !o)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
        >
          <div style={styles.avatar}>{initial}</div>
          <div style={styles.profileMeta}>
            <span style={styles.profileName}>{displayName}</span>
            <span style={styles.profileSub}>{profileSub}</span>
          </div>
          <span
            aria-hidden="true"
            style={{
              ...styles.profileChevron,
              transform: menuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >▾</span>
        </button>
      </div>
    </aside>
  )
}

// ─────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────

const c = theme.colors
const f = theme.fonts

const styles = {
  sidebar: {
    width: '232px',
    background: c.navy,
    color: 'rgba(255,255,255,0.78)',
    display: 'flex',
    flexDirection: 'column',
    padding: '22px 14px 18px',
    position: 'sticky',
    top: 0,
    height: '100vh',
    flexShrink: 0,
    fontFamily: f.body,
  },

  // Brand
  brand: {
    padding: '6px 10px 26px',
    display: 'flex',
    alignItems: 'baseline',
    gap: '4px',
  },
  brandMerq: {
    fontFamily: f.body,
    fontWeight: 700,
    fontSize: '20px',
    color: '#FFFFFF',
    letterSpacing: '-0.01em',
  },
  brandVault: {
    fontFamily: f.display,
    fontWeight: 700,
    fontStyle: 'italic',
    fontSize: '20px',
    color: c.greenMint,
    letterSpacing: '-0.01em',
  },

  // Nav
  nav: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 12px',
    borderRadius: '10px',
    color: 'rgba(255,255,255,0.70)',
    fontSize: '14.5px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background 120ms ease, color 120ms ease, box-shadow 120ms ease',
    textDecoration: 'none',
    minHeight: 'unset',
    minWidth: 'unset',
    fontFamily: f.body,
  },
  navItemActive: {
    background: 'rgba(52, 211, 153, 0.10)',
    color: '#FFFFFF',
    boxShadow: `inset 2px 0 0 ${c.greenMint}`,
  },
  navIcon: {
    flexShrink: 0,
    opacity: 0.9,
  },

  // Foot
  foot: {
    marginTop: 'auto',
    paddingTop: '14px',
    borderTop: '1px solid rgba(255,255,255,0.06)',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  profileCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px',
    borderRadius: '10px',
    cursor: 'pointer',
    background: 'transparent',
    border: 'none',
    color: 'inherit',
    width: '100%',
    textAlign: 'left',
    fontFamily: f.body,
  },
  avatar: {
    width: '30px',
    height: '30px',
    borderRadius: '50%',
    background: c.greenMint,
    color: c.navy,
    display: 'grid',
    placeItems: 'center',
    fontWeight: 700,
    fontSize: '13px',
    flexShrink: 0,
  },
  profileMeta: {
    display: 'flex',
    flexDirection: 'column',
    lineHeight: 1.2,
    flex: 1,
    minWidth: 0,
  },
  profileName: {
    fontSize: '13.5px',
    color: 'rgba(255,255,255,0.92)',
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  profileSub: {
    fontSize: '11.5px',
    color: 'rgba(255,255,255,0.45)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  profileChevron: {
    color: 'rgba(255,255,255,0.45)',
    flexShrink: 0,
    fontSize: '11px',
    lineHeight: 1,
    transition: 'transform 160ms ease',
  },

  // Dropdown menu
  menu: {
    position: 'absolute',
    bottom: 'calc(100% - 6px)',
    left: 0,
    right: 0,
    background: c.navy2,
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '10px',
    padding: '6px',
    boxShadow: '0 10px 30px -8px rgba(0,0,0,0.5)',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    zIndex: 10,
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '9px 10px',
    borderRadius: '8px',
    border: 'none',
    background: 'transparent',
    color: 'rgba(255,255,255,0.82)',
    fontSize: '13.5px',
    fontWeight: 500,
    fontFamily: f.body,
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
    transition: 'background 120ms ease',
  },
  menuDivider: {
    height: '1px',
    background: 'rgba(255,255,255,0.06)',
    margin: '4px 0',
  },
}
